import { Platform } from 'react-native';

// Get platform-specific fallback fonts
const getFallbackFonts = () => {
  if (Platform.OS === 'web') {
    return 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  } else if (Platform.OS === 'ios') {
    return 'system-ui, -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text"';
  } else {
    return 'system-ui, "Roboto", "Noto Sans", sans-serif';
  }
};

const fallbackFonts = getFallbackFonts();

// Env-overridable font family name prefixes (see src/config/font.config.ts)
// — default to Tajawal for both Latin and Arabic (it natively supports both
// scripts). Only the WEB CSS string prefix is read here; native font names
// are resolved separately via fonts.ts + font.config.ts's family-key
// selection (used in LocaleContext).
const PRIMARY_PREFIX = process.env.EXPO_PUBLIC_FONT_FAMILY_PRIMARY_PREFIX || 'Tajawal';
const ARABIC_PREFIX = process.env.EXPO_PUBLIC_FONT_FAMILY_ARABIC_PREFIX || 'Tajawal';

export const typography = {
  fontFamily: {
    primary: `${PRIMARY_PREFIX}-Regular, ${fallbackFonts}`,
    heading: `${PRIMARY_PREFIX}-Bold, ${fallbackFonts}`,
    light: `${PRIMARY_PREFIX}-Light, ${fallbackFonts}`,
    medium: `${PRIMARY_PREFIX}-Medium, ${fallbackFonts}`,
    semibold: `${PRIMARY_PREFIX}-SemiBold, ${fallbackFonts}`,
    bold: `${PRIMARY_PREFIX}-Bold, ${fallbackFonts}`,
    black: `${PRIMARY_PREFIX}-Black, ${fallbackFonts}`,
    regular: `${PRIMARY_PREFIX}-Regular, ${fallbackFonts}`,
    arabic: `${ARABIC_PREFIX}-Regular, ${fallbackFonts}`,
    arabicThin: `${ARABIC_PREFIX}-Thin, ${fallbackFonts}`,
    arabicHeading: `${ARABIC_PREFIX}-Bold, ${fallbackFonts}`,
    arabicLight: `${ARABIC_PREFIX}-Thin, ${fallbackFonts}`,
    arabicMedium: `${ARABIC_PREFIX}-Medium, ${fallbackFonts}`,
    arabicSemibold: `${ARABIC_PREFIX}-SemiBold, ${fallbackFonts}`,
    arabicBold: `${ARABIC_PREFIX}-Bold, ${fallbackFonts}`,
    arabicBlack: `${ARABIC_PREFIX}-Black, ${fallbackFonts}`,
  },
  fontSize: {
    xs: 12,
    sm: 14,
    base: 16,
    lg: 18,
    xl: 20,
    '2xl': 24,
    '3xl': 30,
    '4xl': 36,
    '5xl': 48,
    '6xl': 60,
  },
  fontWeight: {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    black: '900',
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
  letterSpacing: {
    tight: -0.5,
    normal: 0,
    wide: 0.5,
  },
};

// typography.lineHeight.* stores unitless CSS-style ratios (1.25/1.5/1.75).
// React Native's `lineHeight` style property is NOT unitless like CSS — it
// expects an absolute point value. Multiplying by fontSize here converts the
// ratio into the absolute value RN actually needs; storing the raw ratio
// (as this file previously did) rendered a ~1-2pt line box, visually
// collapsing text to near-zero height.
export const textStyles = {
  h1: {
    fontSize: typography.fontSize['4xl'],
    fontWeight: typography.fontWeight.black,
    lineHeight: typography.fontSize['4xl'] * typography.lineHeight.tight,
    letterSpacing: typography.letterSpacing.tight,
    fontFamily: typography.fontFamily.black,
  },
  h2: {
    fontSize: typography.fontSize['3xl'],
    fontWeight: typography.fontWeight.bold,
    lineHeight: typography.fontSize['3xl'] * typography.lineHeight.tight,
    letterSpacing: typography.letterSpacing.tight,
    fontFamily: typography.fontFamily.bold,
  },
  h3: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.semibold,
    lineHeight: typography.fontSize['2xl'] * typography.lineHeight.normal,
    fontFamily: typography.fontFamily.semibold,
  },
  h4: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.fontSize.xl * typography.lineHeight.normal,
    fontFamily: typography.fontFamily.medium,
  },
  body: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.normal,
    lineHeight: typography.fontSize.base * typography.lineHeight.relaxed,
    fontFamily: typography.fontFamily.primary,
  },
  caption: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.normal,
    lineHeight: typography.fontSize.sm * typography.lineHeight.normal,
    fontFamily: typography.fontFamily.primary,
  },
};
